import React, { useContext } from 'react';
import Carousel from 'components/sections/carousel/Carousel';
import SchoolCard from 'components/sections/lists/ClickPortalResults/result-card/schoolCard/SchoolCard';
import { CategoryGuid, MultiRowSchoolListingResults } from 'types';
import { FIELD_NAMES, BREAKPOINT_MAP } from 'consts';
import GlobalContext from 'hooks/contexts/GlobalContext';
import getFormSelectionObjects from 'components/form-wizards/click-portal-search/utils/getFormSelectionObjects';
import get from 'lodash/get';
import { handleCarouselInteractionTracking } from 'utils/analytics/handleCarouselInteractionTracking';

export interface ClickPortalResultRowsListProps {
  resultRow: MultiRowSchoolListingResults['resultRows'][number];
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  isLoading: boolean;
  wpTitle?: string;
  wpSubTitle?: string;
  rowIndex: number;
}

export default function ClickPortalResultRowsList({
  resultRow,
  scrollRef,
  isLoading,
  wpTitle,
  wpSubTitle,
  rowIndex,
}: ClickPortalResultRowsListProps): React.JSX.Element | null {
  const {
    programTaxonomyMap,
    windowSize: { currentBreakpoint },
    siteMeta: { schoolCode },
  } = useContext(GlobalContext);

  const rowSelection = {
    [FIELD_NAMES.CLICK_PORTAL_DEGREES]:
      resultRow?.filterKeyDerivedValues?.degree_level_guid,
    [FIELD_NAMES.CLICK_PORTAL_SUBJECTS]:
      resultRow?.filterKeyDerivedValues?.parent_category_guid,
    [FIELD_NAMES.CLICK_PORTAL_CONCENTRATIONS]:
      resultRow?.filterKeyDerivedValues?.category_guid,
  };
  const { degree, category, subject } = getFormSelectionObjects(
    rowSelection,
    programTaxonomyMap
  );

  const hasSelection = category && degree && subject;

  function getTitle(): string {
    if (wpTitle) {
      return wpTitle;
    }

    if (resultRow?.title) {
      return resultRow?.title;
    }

    if (hasSelection) {
      return `Online ${get(category, 'label')} Degree Programs`;
    }
    return resultRow?.title;
  }

  function getSubTitle(): string {
    if (wpSubTitle) {
      return wpSubTitle;
    }

    if (resultRow?.subTitle) {
      return resultRow?.subTitle;
    }

    if (hasSelection) {
      return 'Explore our most popular schools';
    }
    return resultRow?.subTitle;
  }
  // If there are no results, don't render the list
  if (!isLoading && resultRow?.results?.length === 0) {
    return null;
  }

  const stepSize = currentBreakpoint === BREAKPOINT_MAP.MOBILE.label ? 1 : 2;

  return (
    <div
      className="clickPortalResultRowsSection__list"
      ref={scrollRef as React.RefObject<HTMLDivElement>}
    >
      <div className="clickPortalResultRowsSection__listHeadings">
        <h3 className="clickPortalResultRowsSection__listTitle">
          {getTitle()}
        </h3>
        <p className="clickPortalResultRowsSection__listSubTitle">
          {getSubTitle()}
        </p>
      </div>

      <Carousel
        scrollToActiveSlide={false}
        stepSize={stepSize}
        onCarouselIndexChangeAttempt={(args) =>
          handleCarouselInteractionTracking(
            rowIndex,
            schoolCode,
            args,
            resultRow.schoolImpressionGroupGuid
          )
        }
      >
        {resultRow?.results?.map((school, cardIndex) => {
          return (
            <SchoolCard
              rowCategoryGuid={
                (resultRow?.filterKeyDerivedValues
                  ?.category_guid as CategoryGuid) ?? null
              }
              rowHasTags={resultRow?.hasTags}
              index={cardIndex}
              rowIndex={rowIndex}
              // Its possible impression has not been loaded yet so leverage index until it is
              key={`${school.id}_${school.impressionGuid || cardIndex}`}
              schoolInfo={school}
              // we will always have results, either server renderd or client
              isLoading={false}
              badge={cardIndex === 0 ? "Editor's Choice" : ''}
            />
          );
        })}
      </Carousel>
    </div>
  );
}
